import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { TextareaAutosize as BaseTextareaAutosize } from '@mui/base/TextareaAutosize';
import { styled } from '@mui/material/styles';
import './custom-input-textarea.scss';

interface InputTextAreaProps {
  title?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  minRows?: number;
  maxRows?: number;
  focusInput?: boolean;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) => any;
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) => any;
  inputTextAreaStyle?: React.CSSProperties;
  containerStyles?: React.CSSProperties;
  inputFieldProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;

  // New props for adornments
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  adornmentPosition?: 'top' | 'center' | 'bottom'; // Vertical alignment
}

const TextareaContainer = styled('div')`
  position: relative;
  display: flex;
  align-items: flex-start;
  border: 1px solid #d9d9d9;
  border-radius: 21px;
  background: #fff;

  &:hover {
    border-color: #8c8c8c;
  }

  &:focus-within {
    border-color: #33a7e8;
  }
`;

const Textarea = styled(BaseTextareaAutosize)`
  box-sizing: border-box;
  width: 100%;
  font-size: 14px;
  padding: 6px;
  margin: 2px;
  color: #000000;
  background: transparent;
  border: none;
  resize: none;
  outline: none;

  &::placeholder {
    color: #bfc0c2;
    opacity: 1;
    font-size: 14px;
    font-family: SF-Pro-Display-Regular;
    font-weight: 400;
  }
`;

const AdornmentWrapper = styled('div')<{
  position: 'top' | 'center' | 'bottom';
}>`
  display: flex;
  align-self: ${(props) =>
    props.position === 'top'
      ? 'flex-start'
      : props.position === 'center'
        ? 'center'
        : 'flex-end'};
  padding: 8px;
  flex-shrink: 0;
`;

export const CustomInputTextArea = forwardRef<
  HTMLTextAreaElement,
  InputTextAreaProps
>(
  (
    {
      title,
      placeholder = '',
      value,
      minRows,
      maxRows,
      onBlur,
      focusInput = false,
      required = false,
      disabled = false,
      onFocus,
      inputTextAreaStyle,
      containerStyles,
      inputFieldProps,
      onChange = () => {},
      startAdornment,
      endAdornment,
      adornmentPosition = 'center',
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
      if (focusInput && ref && typeof ref !== 'function') {
        ref.current?.focus();
      } else if (!focusInput && ref && typeof ref !== 'function') {
        ref.current?.blur();
      }
    }, [focusInput, ref]);

    return (
      <div
        className="input_text_area tertiary-font-regular"
        style={containerStyles}
      >
        {title && (
          <span
            className={`input_text_area__title ${
              isFocused ? 'input_text_area__focused' : ''
            }`}
          >
            {title}
            {required && <span className="input_text__asterisk">*</span>}
          </span>
        )}

        <TextareaContainer>
          {startAdornment && (
            <AdornmentWrapper position={adornmentPosition}>
              {startAdornment}
            </AdornmentWrapper>
          )}

          <Textarea
            className="tertiary-font-regular"
            style={inputTextAreaStyle}
            value={value || ''} // Use prop directly
            required={required}
            disabled={disabled}
            minRows={minRows}
            maxRows={maxRows}
            ref={ref}
            onFocus={(e) => {
              setIsFocused(true);
              onFocus && onFocus(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              onBlur && onBlur(e);
            }}
            onChange={(e) => onChange(e)}
            placeholder={placeholder}
            {...inputFieldProps}
          />

          {endAdornment && (
            <AdornmentWrapper position={adornmentPosition}>
              {endAdornment}
            </AdornmentWrapper>
          )}
        </TextareaContainer>
      </div>
    );
  }
);
